<script setup lang="ts">
import { ref, computed } from 'vue';
import IconsUpscaleFeature from '../../icons/UpscaleFeature.vue';
import ImageLazy from '@/components/ImageLazy.vue';

// Hover state and mouse position
const imageContainerRef = ref<HTMLElement | null>(null);
const isHovering = ref(false);
const isRippleComplete = ref(false); // 波纹动画是否完成
const mouseX = ref(50); // percentage 0-100
const mouseY = ref(50); // percentage 0-100

let rippleTimer: ReturnType<typeof setTimeout> | null = null;

const RIPPLE_DURATION = 800; // 波纹动画时长 (ms)

const handleMouseEnter = () => {
  isHovering.value = true;
  isRippleComplete.value = false;

  // 波纹动画完成后，开启放大功能
  if (rippleTimer) clearTimeout(rippleTimer);
  rippleTimer = setTimeout(() => {
    isRippleComplete.value = true;
  }, RIPPLE_DURATION);
};

const handleMouseLeave = () => {
  isHovering.value = false;
  isRippleComplete.value = false;
  // Reset to center
  mouseX.value = 50;
  mouseY.value = 50;

  if (rippleTimer) {
    clearTimeout(rippleTimer);
    rippleTimer = null;
  }
};

const handleMouseMove = (event: MouseEvent) => {
  if (!imageContainerRef.value || !isHovering.value) return;
  const rect = imageContainerRef.value.getBoundingClientRect();
  // Calculate mouse position as percentage
  mouseX.value = ((event.clientX - rect.left) / rect.width) * 100;
  mouseY.value = ((event.clientY - rect.top) / rect.height) * 100;
};

// Compute transform for the zoomed image
// Only apply zoom after ripple animation completes
const imageTransform = computed(() => {
  if (!isRippleComplete.value) {
    return 'scale(1) translate(0, 0)';
  }
  // Calculate offset: at edges (0% or 100%), we want max offset; at center (50%), no offset
  // With scale 2, the image is twice as large, so we can shift by up to 25% in each direction
  const offsetX = (50 - mouseX.value) * 0.5; // -25% to +25%
  const offsetY = (50 - mouseY.value) * 0.5; // -25% to +25%
  return `scale(2) translate(${offsetX}%, ${offsetY}%)`;
});

// Ripple clip-path: circle expands from center
const rippleClipPath = computed(() => {
  if (!isHovering.value) {
    return 'circle(0% at 50% 50%)';
  }
  return 'circle(150% at 50% 50%)';
});
</script>

<template>
  <div
    ref="imageContainerRef"
    class="group relative rounded-3xl border border-black/5 overflow-hidden cursor-zoom-in"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @mousemove="handleMouseMove"
  >
    <!-- Background image layers -->
    <div class="absolute inset-0 overflow-hidden">
      <!-- Blurry/pixelated image (bottom layer, always visible) -->
      <ImageLazy
        src="/images/upscale/minimal.jpg"
        alt="Low resolution preview"
        class="absolute inset-0 w-full h-full"
        image-class="w-full h-full object-cover upscale-mosaic-image"
      />
      <!-- Clear image (top layer, with ripple reveal + zoom) -->
      <ImageLazy
        src="/images/upscale/clear.webp"
        alt="HD Upscale preview"
        class="absolute inset-0 w-full h-full"
        :image-class="
          [
            'w-full h-full object-cover upscale-clear-image',
            isRippleComplete ? 'is-zooming' : '',
          ].join(' ')
        "
        :style="{
          clipPath: rippleClipPath,
          transform: imageTransform,
        }"
      />
      <!-- Gradient overlay for text readability -->
      <div
        class="absolute inset-0 pointer-events-none z-10"
        style="
          background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0.3) 0%,
            rgba(0, 0, 0, 0.2) 50%,
            transparent 100%
          );
        "
      />
    </div>
    <!-- Content -->
    <div class="relative z-10 p-6 md:p-8 min-h-[400px] flex flex-col">
      <IconsUpscaleFeature class="w-5 h-5 text-white mb-4" />
      <p class="text-lg md:text-xl leading-[1.4]">
        <span class="text-white">{{ $t('landing_home.cards.upscale.primary') }}</span>
        <span class="text-white/60">{{ $t('landing_home.cards.upscale.secondary') }}</span>
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Upscale mosaic effect - low-res image with pixelated rendering */
.upscale-mosaic-image {
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

/* Clear image with ripple reveal animation */
.upscale-clear-image {
  /* Ripple animation for clip-path */
  transition:
    clip-path 800ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 300ms ease-out;
}

/* When zooming is active, use faster transform transition */
.upscale-clear-image.is-zooming {
  transition:
    clip-path 2000ms cubic-bezier(0.4, 0, 0.2, 1),
    transform 150ms ease-out;
}
</style>
